import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Typography, Button, Box } from "@mui/material";
import Download from "@mui/icons-material/Download";
import { toastr } from "react-redux-toastr";
import PageLayout from "@/components/layouts/page/PageLayout";
import { GlobalContext } from "@/helpers/contexts/contexts";
import LocationManagementFilters from "../components/LocationManagementFilters";
import LocationManagementTable from "../components/LocationManagementTable";
import LocationFormModal from "../components/LocationFormModal";
import { useLocationManagement } from "../hooks/useLocationManagement";
import { locationManagementService } from "../services/locationManagementService";
import BackLink from "@/components/BackLink";
import useInfiniteFetcher from "@/helpers/hooks/useInfiniteFetcher";
import {
  buildGroupTypeSections,
  flattenGroupSections,
} from "../utils/locationManagementGroupTypes";

const PARTICIPANT_FILTER_PAGE_SIZE = 12;

const LocationManagementPage = () => {
  const { selectedBrand, loggedInUser } = useContext(GlobalContext);

  const {
    filters,
    requestParams,
    handleFilterChange,
    handlePageChange,
    handleSort,
  } = useLocationManagement();

  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [groupSections, setGroupSections] = useState([]);

  const participantReqData = useMemo(
    () =>
      selectedBrand?.id
        ? { brand_id: selectedBrand.id, row: PARTICIPANT_FILTER_PAGE_SIZE }
        : null,
    [selectedBrand?.id]
  );

  const {
    data: participantPages,
    isValidating: isParticipantsLoading,
    mutate: refreshParticipants,
    size: participantPage,
    setSize: setParticipantPage,
  } = useInfiniteFetcher(
    participantReqData,
    locationManagementService.getActiveParticipants as any,
    { revalidateOnFocus: false }
  );
  const typedParticipantPages = participantPages as any[] | undefined;

  const participantOptions = useMemo(() => {
    const seen = new Set();
    return (typedParticipantPages || [])
      .flatMap((response) =>
        Array.isArray(response?.data)
          ? response.data
          : response?.data?.data || []
      )
      .map((participant) => ({
        value: String(participant.fk_participant_id),
        label: `${
          participant.participant_first_name || participant.first_name || ""
        } ${
          participant.participant_last_name || participant.last_name || ""
        }`.trim(),
        participant_first_name:
          participant.participant_first_name || participant.first_name,
        participant_last_name:
          participant.participant_last_name || participant.last_name,
        participant_brand_id:
          participant.participant_unique_id ||
          participant.system_generated_participant_id ||
          "",
        participant_id: participant.system_generated_participant_id || "",
        email_address: participant.participant_email || participant.email,
        phone: participant.participant_phone || participant.mobile_no || "",
      }))
      .filter((option) => {
        const isValid = option.value !== "undefined" && option.label;
        if (!isValid || seen.has(option.value)) return false;
        seen.add(option.value);
        return true;
      });
  }, [typedParticipantPages]);

  const loadFilterOptions = useCallback(async () => {
    if (!selectedBrand?.id) return;
    try {
      const response = await locationManagementService.getGroupAndGroupTypes(
        selectedBrand.id
      );
      const groupTypeData = response.data || {};
      setGroupSections(
        buildGroupTypeSections({
          groupTypes: groupTypeData.groupTypes || [],
          groups: groupTypeData.groups || [],
        })
      );
    } catch (_e) {}
  }, [selectedBrand?.id]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  const groupOptions = useMemo(
    () => flattenGroupSections(groupSections),
    [groupSections]
  );

  const fetchLocations = useCallback(async () => {
    setIsLoading(true);
    try {
      const response =
        await locationManagementService.getLocationManagementList({
          ...requestParams,
          brandId: selectedBrand?.id,
          userId: loggedInUser?.id,
        });
      setLocations(response.data.result || []);
      setTotalPages(response.data.pageCount || 0);
      setTotalRecords(response.data.recordsTotal || 0);
    } catch (_e) {
    } finally {
      setIsLoading(false);
    }
  }, [requestParams, selectedBrand?.id, loggedInUser?.id]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const onPageChange = useCallback(
    (page) => handlePageChange(page - 1),
    [handlePageChange]
  );
  const onSortChange = useCallback(
    (sortBy, sortByDir) => handleSort(sortBy, sortByDir),
    [handleSort]
  );

  const handleEditClick = async (location) => {
    try {
      const locationId = location?.fk_location_id;
      if (!locationId) throw new Error("Location id is missing");
      const response = await locationManagementService.getLocationDetail(
        locationId,
        selectedBrand?.id
      );
      setSelectedLocation({
        ...location,
        ...(response.data?.data || {}),
      });
    } catch (_e) {
      setSelectedLocation(location);
    }
    setModalOpen(true);
  };

  const handleAddClick = () => {
    setSelectedLocation(null);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedLocation(null);
  };

  const handleSave = (shouldRefreshParticipants) => {
    fetchLocations();
    if (shouldRefreshParticipants) {
      loadFilterOptions();
      setParticipantPage(1);
      refreshParticipants();
    }
  };

  const loadMoreParticipants = useCallback(() => {
    const lastPage =
      typedParticipantPages?.[typedParticipantPages.length - 1]?.data;
    const participants = Array.isArray(lastPage)
      ? lastPage
      : lastPage?.data || [];
    const hasMore = lastPage?.pageCount
      ? participantPage < lastPage.pageCount
      : participants.length >= PARTICIPANT_FILTER_PAGE_SIZE;

    if (hasMore && !isParticipantsLoading) {
      setParticipantPage((prev) => prev + 1);
    }
  }, [
    isParticipantsLoading,
    participantPage,
    typedParticipantPages,
    setParticipantPage,
  ]);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await locationManagementService.exportLocationsXlsx(
        selectedBrand?.id,
        filters?.groupId,
        filters?.participantId
      );
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      const disposition = response.headers["content-disposition"];
      const filename =
        disposition?.match(/filename[^;=\n]*=(['"]?)([^'"\n;]*)\1/)?.[2] ||
        `locations_sheet_${Date.now()}.xlsx`;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toastr.success("Success", "Template downloaded successfully.");
    } catch (_e) {
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <PageLayout className="mx-0 mx-sm-2">
      <BackLink />
      <Box className="d-flex justify-content-between align-items-center mb-4">
        <Typography variant="h5" className="widget-title">
          Location Management
        </Typography>
      </Box>

      <LocationManagementFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        groupSections={groupSections}
        participantOptions={participantOptions}
        onParticipantListEndReached={loadMoreParticipants}
        actionSlot={
          <>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddClick}
              fullWidth
              className="py-2"
            >
              Add Location
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleExport}
              disabled={isExporting}
              fullWidth
              className="py-2"
            >
              {isExporting ? "Exporting..." : "Download Template"}
            </Button>
          </>
        }
      />

      <LocationManagementTable
        brandId={selectedBrand?.id}
        data={locations}
        isLoading={isLoading}
        pagination={{
          currentPage: filters.pageIndex + 1,
          pageSize: filters.pageSize,
          totalPages,
          totalRecords,
        }}
        onPageChange={onPageChange}
        onSortChange={onSortChange}
        onEditClick={handleEditClick}
      />

      <LocationFormModal
        open={modalOpen}
        onClose={handleModalClose}
        location={selectedLocation}
        onSave={handleSave}
        groupOptions={groupOptions}
        groupSections={groupSections}
        participantOptions={participantOptions}
        onParticipantListEndReached={loadMoreParticipants}
      />
    </PageLayout>
  );
};

export default LocationManagementPage;
